import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Select, Table, Space, message, Spin, Tag } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { getData, postData } from "../../../apiServices/common";
import {
  GET_ALL_UNITS,
  getItemCategoryAssignedUnitsUrl,
  postItemCategoryAssignUnitsUrl,
} from "../../../apiServices/API_ENDPOINTS";

const AssignUnitsModal = ({
  open,
  onClose,
  category,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allUnits, setAllUnits] = useState([]);
  const [rows, setRows] = useState([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [addUnitIds, setAddUnitIds] = useState([]);

  const categoryId = category?.id;

  const resetState = useCallback(() => {
    setRows([]);
    setPendingDeleteIds([]);
    setAddUnitIds([]);
    setAllUnits([]);
  }, []);

  const loadData = useCallback(async () => {
    if (!categoryId) return;
    setLoading(true);
    setRows([]);
    setPendingDeleteIds([]);
    setAddUnitIds([]);
    try {
      const [assignedRes, unitsRes] = await Promise.all([
        getData(getItemCategoryAssignedUnitsUrl(categoryId), false, {
          update_view: 1,
        }),
        getData(GET_ALL_UNITS, false, { per_page: 5000 }),
      ]);

      if (assignedRes?.data?.code === 200) {
        const data = assignedRes.data.data;
        const maps = data?.item_category_units || [];
        setRows(
          maps.map((m) => ({
            key: String(m.id),
            mapId: m.id,
            unitId: m.unit_id,
            unitName: m.unit?.name,
            baseUnit: m.unit?.base_unit,
          })),
        );
      } else {
        message.error(assignedRes?.data?.message || "Failed to load units");
      }

      if (unitsRes?.data?.code === 200) {
        setAllUnits(unitsRes?.data?.data?.data || []);
      }
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    if (open && categoryId) {
      loadData();
    }
    if (!open) {
      resetState();
    }
  }, [open, categoryId, loadData, resetState]);

  const assignedUnitIds = new Set(rows.map((r) => r.unitId));
  const addOptions = allUnits.filter((u) => !assignedUnitIds.has(u.id));

  const handleAdd = () => {
    if (!addUnitIds?.length) {
      message.warning("Select one or more units to add");
      return;
    }
    const ts = Date.now();
    const newRows = addUnitIds
      .map((id, idx) => {
        const unit = allUnits.find((u) => u.id === id);
        if (!unit) return null;
        return {
          key: `new-${id}-${ts}-${idx}`,
          mapId: 0,
          unitId: unit.id,
          unitName: unit.name,
          baseUnit: unit.base_unit,
        };
      })
      .filter(Boolean);
    if (!newRows.length) {
      message.warning("No valid units selected");
      return;
    }
    setRows((prev) => {
      const existing = new Set(prev.map((r) => r.unitId));
      const toAppend = newRows.filter((r) => !existing.has(r.unitId));
      if (!toAppend.length && newRows.length) {
        Promise.resolve().then(() =>
          message.info("Selected units are already assigned"),
        );
        return prev;
      }
      return toAppend.length ? [...prev, ...toAppend] : prev;
    });
    setAddUnitIds([]);
  };

  const handleRemoveRow = (record) => {
    if (record.mapId > 0) {
      setPendingDeleteIds((prev) => [...prev, record.mapId]);
    }
    setRows((prev) => prev.filter((r) => r.key !== record.key));
  };

  const handleSave = async () => {
    if (!categoryId) return;

    const rowUnitIds = new Set(rows.map((r) => Number(r.unitId)));
    const fromTable = rows.map((r) => ({
      id: r.mapId > 0 ? r.mapId : 0,
      unit_id: Number(r.unitId),
      is_delete: 0,
    }));
    const fromMultiSelect = (addUnitIds || [])
      .filter((id) => id != null && !rowUnitIds.has(Number(id)))
      .map((id) => ({
        id: 0,
        unit_id: Number(id),
        is_delete: 0,
      }));
    const deleteMaps = pendingDeleteIds.map((id) => ({
      id,
      unit_id: 0,
      is_delete: 1,
    }));

    const unit_maps = [...fromTable, ...fromMultiSelect, ...deleteMaps];

    if (!unit_maps.length) {
      message.warning(
        "Select one or more units (or use Add selected), then save",
      );
      return;
    }

    setSaving(true);
    try {
      const res = await postData(postItemCategoryAssignUnitsUrl(categoryId), {
        unit_maps,
      });

      if (res && res.code === 200) {
        message.success(res.message || "Units updated");
        setAddUnitIds([]);
        onSaved?.();
        onClose();
      } else if (res !== false) {
        message.error(res?.message || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Unit",
      dataIndex: "unitName",
      key: "unitName",
    },
    {
      title: "Base unit",
      key: "baseUnit",
      width: 120,
      render: (_, record) =>
        record.baseUnit === "Yes" ? (
          <Tag color="green">Yes</Tag>
        ) : (
          <Tag>No</Tag>
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 90,
      align: "right",
      render: (_, record) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveRow(record)}
        />
      ),
    },
  ];

  return (
    <Modal
      title={
        category?.name
          ? `Assign units — ${category.name}`
          : "Assign units"
      }
      open={open}
      onCancel={onClose}
      width={640}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={loading}
          onClick={handleSave}
        >
          Save
        </Button>,
      ]}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Table
            size="small"
            pagination={false}
            columns={columns}
            dataSource={rows}
            locale={{ emptyText: "No units assigned" }}
          />
          <Space direction="vertical" style={{ width: "100%" }} size="small">
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              placeholder="Multi-select units, then Save (or add to table first)"
              style={{ width: "100%" }}
              value={addUnitIds}
              onChange={setAddUnitIds}
              options={addOptions.map((u) => ({
                value: u.id,
                label: u.name,
              }))}
              allowClear
              maxTagCount="responsive"
            />
            <Button type="dashed" onClick={handleAdd} block>
              Add selected to table
            </Button>
          </Space>
        </Space>
      </Spin>
    </Modal>
  );
};

export default AssignUnitsModal;
